import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getPublicSettings } from '../api/client';

interface CmsSettings {
  [key: string]: string;
}

interface CmsContextType {
  settings: CmsSettings;
  loading: boolean;
  refresh: () => Promise<void>;
  get: (key: string, fallback?: string) => string;
  getJson: (key: string, fallback?: any) => any;
}

const CmsContext = createContext<CmsContextType>({
  settings: {},
  loading: true,
  refresh: async () => {},
  get: (key: string, fallback = '') => fallback,
  getJson: () => null,
});

export function CmsProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [settings, setSettings] = useState<CmsSettings>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await getPublicSettings();
      const list = Array.isArray(res.settings)
        ? res.settings
        : Array.isArray(res.data?.settings)
          ? res.data.settings
          : [];
      const map: CmsSettings = {};
      list.forEach((s: any) => {
        if (s?.key) map[s.key] = s.value ?? '';
      });
      setSettings(map);
    } catch {
      // silent fail — fallbacks will be used
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const get = useCallback(
    (key: string, fallback = '') => settings[key] ?? fallback,
    [settings]
  );

  const getJson = useCallback(
    (key: string, fallback: any = null) => {
      const val = settings[key];
      if (!val) return fallback;
      try {
        return JSON.parse(val);
      } catch {
        return fallback;
      }
    },
    [settings]
  );

  return (
    <CmsContext.Provider value={{ settings, loading, refresh: load, get, getJson }}>
      {children}
    </CmsContext.Provider>
  );
}

export function useCms(): CmsContextType {
  return useContext(CmsContext);
}
